import {
  CheckOutlined,
  ClockCircleOutlined,
  ExclamationOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { Company } from '../models/company-profile.model';
import {
  CompanyMetrics,
  companyDescriptionText,
  companyWebsiteLabel,
  companyWebsiteUrl,
  getCompanyReadiness,
} from './company-workspace.utils';

interface Props {
  company: Company;
  logo: string;
  metrics: CompanyMetrics;
  ownerName: string;
}

const CompanyOverview = ({ company, logo, metrics, ownerName }: Props) => {
  const readiness = getCompanyReadiness(company, Boolean(logo), metrics);
  const completed = readiness.filter((item) => item.complete).length;
  const description = companyDescriptionText(company.aboutUs);
  const websiteUrl = companyWebsiteUrl(company.website);

  return (
    <div className="company-overview">
      <section className="company-panel" aria-labelledby="company-info-title">
        <header className="company-panel__header">
          <div>
            <h2 id="company-info-title">Company information</h2>
            <p>Details used across jobs and candidate communications.</p>
          </div>
        </header>

        <dl className="company-information">
          <div>
            <dt>Company name</dt>
            <dd>{company.name || 'Not provided'}</dd>
          </div>
          <div>
            <dt>Website</dt>
            <dd>
              {websiteUrl ? (
                <a href={websiteUrl} target="_blank" rel="noreferrer">
                  {companyWebsiteLabel(company.website)}
                  <ExportOutlined aria-hidden />
                </a>
              ) : (
                'Not provided'
              )}
            </dd>
          </div>
          <div>
            <dt>Workspace owner</dt>
            <dd>{ownerName}</dd>
          </div>
          <div>
            <dt>Profile status</dt>
            <dd>{completed === readiness.length ? 'Ready' : 'In progress'}</dd>
          </div>
          <div className="company-information__about">
            <dt>About the company</dt>
            <dd>
              {description ||
                'Add a short company story so recruiters and candidates have the right context.'}
            </dd>
          </div>
        </dl>
      </section>

      <aside
        className="company-panel company-readiness"
        aria-labelledby="company-readiness-title"
      >
        <header className="company-panel__header">
          <div>
            <h2 id="company-readiness-title">Workspace readiness</h2>
            <p>Finish the essentials for a complete recruiting workspace.</p>
          </div>
          <span className="company-readiness__count">
            {completed} of {readiness.length}
          </span>
        </header>
        <ul>
          {readiness.map((item) => (
            <li key={item.key}>
              <span>{item.label}</span>
              <span
                className={`company-readiness__state${
                  item.complete
                    ? ' company-readiness__state--complete'
                    : item.unavailable
                      ? ' company-readiness__state--unknown'
                      : ''
                }`}
                aria-label={
                  item.complete
                    ? 'Complete'
                    : item.unavailable
                      ? 'Status unavailable'
                      : 'Needs attention'
                }
              >
                {item.complete ? (
                  <CheckOutlined aria-hidden />
                ) : item.unavailable ? (
                  <ClockCircleOutlined aria-hidden />
                ) : (
                  <ExclamationOutlined aria-hidden />
                )}
              </span>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
};

export default CompanyOverview;
